import { useEffect, useState } from 'react';
import api from '../../api/axios';
import StatusBadge from '../../components/StatusBadge';
import RequestComments from '../../components/RequestComments';
import RequestFormModal from '../../components/RequestFormModal';
import { MATERIAL_FIELDS, isFieldRequired } from '../user/materialFieldConfig';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const BASE = {
  material_requests: '/material-requests',
  vendor_requests: '/vendor-requests',
};

const DECISION_STYLE = {
  approved: { dot: '#4f6ef7', bg: '#eef2ff', label: 'Checked' },
  rejected: { dot: '#dc2626', bg: '#fee2e2', label: 'Returned' },
  pending:  { dot: '#f59e0b', bg: '#fef3c7', label: 'Waiting' },
};

const CheckerReview = ({ source }) => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [modalReq, setModalReq] = useState(null);
  const [loading, setLoading] = useState(false);

  const base = BASE[source];
  const heading = source === 'material_requests' ? 'Material Checking'
    : source === 'vendor_requests' ? 'Vendor Checking' : 'Checking';

  const load = () => api.get(`${base}/checking`).then(r => setRequests(r.data)).catch(() => setRequests([]));
  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const openRow = r => setSelected(r);

  const onCommented = comment =>
    setSelected(prev => ({ ...prev, comments: [...(prev.comments || []), comment] }));

  const onModalSaved = updated => {
    setModalReq(prev => ({ ...prev, ...updated }));
    setRequests(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
    if (selected?.id === updated.id) setSelected(prev => ({ ...prev, ...updated }));
  };

  const myTicket = (selected?.approvals || []).find(
    a => (a.approver_email || '').toLowerCase() === (user?.email || '').toLowerCase()
  );
  const canAct = selected?.status === 'pending' && (!myTicket || myTicket.decision === 'pending');

  const handleApprove = async () => {
    // The checker records Material Number for material requests, so it is part of
    // the mandatory check alongside the other material-type fields.
    if (selected.source === 'material_requests') {
      const materialType = String(selected.material_type || '').trim();
      const missing = MATERIAL_FIELDS
        .filter(f => isFieldRequired(f, materialType))
        .filter(f => !String(selected[f.key] ?? '').trim())
        .map(f => f.label);
      if (missing.length) {
        toast.error(`Mandatory field(s) missing for material type ${selected.material_type || '(not set)'}: ${missing.join(', ')}`);
        return;
      }
    }
    setLoading(true);
    try {
      await api.patch(`${base}/${selected.id}/forward`);
      toast.success('Approved and forwarded to approvers');
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not approve request');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    setLoading(true);
    try {
      await api.patch(`${base}/${selected.id}/return`);
      toast.success('Rejected and returned to the user for correction');
      setSelected(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not reject request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.wrap}>
      {/* ── Table ── */}
      <div style={S.tableCard}>
        <div style={S.cardTop}>
          <div style={S.cardTitle}>{heading}</div>
          <div style={S.countBadge}>{requests.length} pending</div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr style={S.thead}>
                {['Request #', 'Title', 'Category', 'Requested By', 'Date', 'Status'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requests.length === 0 && (
                <tr><td colSpan={6} style={S.empty}>No requests waiting for review</td></tr>
              )}
              {requests.map((r, i) => {
                const active = selected?.source === r.source && selected?.id === r.id;
                return (
                  <tr key={`${r.source}-${r.id}`} onClick={() => openRow(r)}
                    style={{ ...S.tr, background: active ? '#ecfeff' : i % 2 === 0 ? '#fff' : '#f8faff', cursor: 'pointer' }}>
                    <td style={{ ...S.td, fontWeight: 700, color: '#4f6ef7' }}>
                      <span onClick={e => { e.stopPropagation(); setModalReq(r); }} style={S.reqLink}>{r.request_number}</span>
                    </td>
                    <td style={{ ...S.td, fontWeight: 600, color: '#0B1554' }}>{r.title}</td>
                    <td style={S.td}><span style={S.catBadge}>{r.category}</span></td>
                    <td style={S.td}>{r.requester_name}</td>
                    <td style={{ ...S.td, color: '#94a3b8' }}>{new Date(r.created_at).toLocaleDateString()}</td>
                    <td style={S.td}><StatusBadge status={r.status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Side Panel ── */}
      {selected && (
        <div style={S.panel}>
          <div style={S.panelHead}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.panelReqNum}>{selected.request_number}</div>
              <div style={S.panelMeta}>{selected.category} · {selected.requester_name}</div>
            </div>
            <button onClick={() => setSelected(null)} style={S.closeBtn}>✕</button>
          </div>

          <div style={S.panelScroll}>
            <div style={S.strip}>
              <div style={S.stripItem}>
                <span style={S.stripLabel}>Status</span>
                <StatusBadge status={selected.status} />
              </div>
              <div style={S.stripItem}>
                <span style={S.stripLabel}>Date</span>
                <span style={S.stripVal}>{new Date(selected.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            <div style={S.section}>
              <div style={S.bigTitle}>{selected.title}</div>
              {selected.description && <div style={S.desc}>{selected.description}</div>}
              <div style={S.openModalHint}>
                Click the <strong>Request #</strong> in the table to view and edit the full form.
              </div>
            </div>

            <div style={S.section}>
              <RequestComments request={selected} canPost={canAct} onPosted={onCommented} />
            </div>

            {selected.approvals?.length > 0 && (
              <div style={S.section}>
                <div style={S.sectionLabel}>Routing</div>
                <div style={S.timeline}>
                  {selected.approvals.map((a, i) => {
                    const ds = DECISION_STYLE[a.decision] || DECISION_STYLE.pending;
                    return (
                      <div key={i} style={S.tlRow}>
                        <div style={S.tlLeft}>
                          <div style={{ ...S.tlDot, background: ds.dot, boxShadow: `0 0 0 3px ${ds.bg}` }} />
                          {i < selected.approvals.length - 1 && <div style={S.tlLine} />}
                        </div>
                        <div style={S.tlBody}>
                          <div style={S.tlName}>{a.approver_name}</div>
                          <div style={S.tlEmail}>{a.approver_email}</div>
                          <span style={{ ...S.tlBadge, background: ds.bg, color: ds.dot }}>{ds.label}</span>
                          {a.remarks && <div style={S.tlRemarks}>"{a.remarks}"</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {canAct ? (
              <div style={S.section}>
                <div style={S.btnRow}>
                  <button style={{ ...S.btn, ...S.btnApprove }} onClick={handleApprove} disabled={loading}>✓ Approve</button>
                  <button style={{ ...S.btn, ...S.btnReject }} onClick={handleReject} disabled={loading}>✕ Reject</button>
                </div>
              </div>
            ) : (
              <div style={S.section}>
                <div style={S.lockNote}>
                  {selected.status !== 'pending'
                    ? `This request is ${selected.status.replace('_', ' ')} — no further action needed.`
                    : 'This request is no longer awaiting your check.'}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {modalReq && (
        <RequestFormModal
          request={modalReq}
          onClose={() => setModalReq(null)}
          editable={modalReq.status === 'pending'}
          onSaved={onModalSaved}
        />
      )}
    </div>
  );
};

const S = {
  wrap: { display: 'flex', gap: 20, alignItems: 'flex-start' },
  tableCard: { flex: 2, background: '#fff', borderRadius: 18, boxShadow: '0 4px 20px rgba(11,21,84,0.06)', overflow: 'hidden', minWidth: 0, border: '1px solid rgba(79,110,247,0.06)' },
  cardTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, padding: '18px 24px', borderBottom: '1px solid #f1f5f9' },
  cardTitle: { fontSize: 14, fontWeight: 800, color: '#0B1554' },
  countBadge: { background: '#ecfeff', color: '#0e7490', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 20, border: '1px solid #a5f3fc' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'linear-gradient(135deg,#083344,#0e7490)' },
  th: { padding: '12px 18px', color: '#F7D506', textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: 0.8, whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' },
  td: { padding: '13px 18px', fontSize: 13.5, color: '#374151' },
  empty: { textAlign: 'center', padding: '40px 20px', color: '#94a3b8', fontSize: 14 },
  catBadge: { background: '#ecfeff', color: '#0e7490', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6 },
  reqLink: { cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', color: '#4f6ef7' },
  panel: { width: 460, flexShrink: 0, background: '#fff', borderRadius: 18, boxShadow: '0 4px 24px rgba(11,21,84,0.12)', overflow: 'hidden', border: '1px solid rgba(79,110,247,0.08)', display: 'flex', flexDirection: 'column', maxHeight: '90vh' },
  panelHead: { background: 'linear-gradient(135deg,#083344,#0e7490)', padding: '16px 20px', display: 'flex', alignItems: 'flex-start', gap: 12, flexShrink: 0 },
  panelReqNum: { color: '#F7D506', fontWeight: 800, fontSize: 14, marginBottom: 2 },
  panelMeta: { color: 'rgba(255,255,255,0.6)', fontSize: 12 },
  closeBtn: { background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', width: 28, height: 28, borderRadius: 7, fontSize: 14, fontWeight: 700, cursor: 'pointer', flexShrink: 0 },
  panelScroll: { overflowY: 'auto', flex: 1 },
  strip: { display: 'flex', borderBottom: '1px solid #f1f5f9' },
  stripItem: { flex: 1, display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 16px', borderRight: '1px solid #f1f5f9' },
  stripLabel: { fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 0.7, textTransform: 'uppercase' },
  stripVal: { fontSize: 13, fontWeight: 700, color: '#0B1554' },
  section: { padding: '14px 20px', borderBottom: '1px solid #f1f5f9' },
  sectionLabel: { fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 10 },
  bigTitle: { fontSize: 14, fontWeight: 700, color: '#0B1554', marginBottom: 4, lineHeight: 1.4 },
  desc: { fontSize: 12.5, color: '#64748b', lineHeight: 1.6, marginBottom: 8 },
  openModalHint: { fontSize: 11.5, color: '#64748b', background: '#f8faff', border: '1px solid #eef1f7', borderRadius: 8, padding: '7px 10px', marginTop: 8 },
  timeline: { display: 'flex', flexDirection: 'column' },
  tlRow: { display: 'flex', gap: 12, marginBottom: 4 },
  tlLeft: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: 16, flexShrink: 0 },
  tlDot: { width: 12, height: 12, borderRadius: '50%', flexShrink: 0, marginTop: 3 },
  tlLine: { width: 2, flex: 1, background: '#e2e8f0', margin: '4px 0' },
  tlBody: { flex: 1, paddingBottom: 12 },
  tlName: { fontSize: 13, fontWeight: 700, color: '#0B1554' },
  tlEmail: { fontSize: 11.5, color: '#64748b', marginBottom: 4 },
  tlBadge: { display: 'inline-block', fontSize: 10.5, fontWeight: 800, padding: '2px 8px', borderRadius: 20, letterSpacing: 0.3 },
  tlRemarks: { fontSize: 12, color: '#475569', fontStyle: 'italic', marginTop: 5, lineHeight: 1.5 },
  btnRow: { display: 'flex', gap: 8 },
  btn: { flex: 1, padding: '11px', color: '#fff', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnApprove: { background: 'linear-gradient(135deg,#0d1a6e,#4f6ef7)', boxShadow: '0 4px 14px rgba(79,110,247,0.4)' },
  btnReject: { background: 'linear-gradient(135deg,#dc2626,#ef4444)', boxShadow: '0 4px 14px rgba(220,38,38,0.4)' },
  lockNote: { fontSize: 12.5, fontWeight: 600, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px' },
};

export default CheckerReview;
